import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LayoutDashboard, Rss, Newspaper, TestTube2, LogOut, Menu, X, AlertOctagon, UserCog, ShieldCheck, Webhook, Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/feeds", label: "Feeds", icon: Rss, testid: "nav-feeds" },
  { to: "/news", label: "News", icon: Newspaper, testid: "nav-news" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications", badgeKey: "unread" },
  { to: "/errors", label: "Errors", icon: AlertOctagon, testid: "nav-errors" },
  { to: "/tester", label: "URL Tester", icon: TestTube2, testid: "nav-tester" },
  { to: "/account", label: "Account", icon: UserCog, testid: "nav-account" },
];
const NAV_SUPER = [
  { to: "/integrations", label: "Integrations", icon: Webhook, testid: "nav-integrations" },
  { to: "/admins", label: "Admins", icon: ShieldCheck, testid: "nav-admins" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const { data: unreadData } = useQuery({
    queryKey: ["notifications-unread"],
    queryFn: async () => (await api.get("/notifications/unread-count")).data,
    refetchInterval: 30000,
    enabled: !!user,
  });
  const unread = unreadData?.unread || 0;

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#050505] text-zinc-100">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 border-r border-zinc-800 bg-[#0A0A0A] flex flex-col transition-transform",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="px-5 py-5 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">Risk Intel</div>
            <div className="text-lg font-mono font-semibold tracking-tight">RSS_SYSTEM</div>
          </div>
          <button
            data-testid="close-sidebar-btn"
            onClick={() => setOpen(false)}
            className="lg:hidden text-zinc-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {[...NAV, ...(user?.role === "super_admin" ? NAV_SUPER : [])].map(({ to, label, icon: Icon, testid, badgeKey }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 text-sm rounded-sm border border-transparent",
                  isActive
                    ? "bg-zinc-900 text-white border-zinc-800"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-900/60"
                )
              }
            >
              <Icon className="h-4 w-4" />
              <span className="font-mono tracking-tight flex-1">{label}</span>
              {badgeKey === "unread" && unread > 0 && (
                <span
                  data-testid="unread-badge"
                  className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500/90 text-white text-[10px] font-mono font-semibold"
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-800 p-3">
          <div className="px-2 py-2 text-xs font-mono text-zinc-400 truncate" data-testid="user-email">
            {user?.email}
          </div>
          <Button
            data-testid="logout-btn"
            variant="outline"
            className="w-full justify-start gap-2 border-zinc-800 bg-transparent hover:bg-zinc-900 rounded-sm"
            onClick={doLogout}
          >
            <LogOut className="h-4 w-4" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar for mobile */}
        <div className="lg:hidden border-b border-zinc-800 bg-[#0A0A0A] px-4 py-3 flex items-center justify-between">
          <button
            data-testid="open-sidebar-btn"
            onClick={() => setOpen(true)}
            className="text-zinc-300"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="font-mono text-sm tracking-tight">RSS_SYSTEM</div>
          <div className="w-6" />
        </div>
        <main className="flex-1 p-4 md:p-6 lg:p-8 max-w-full overflow-x-hidden">{children}</main>
      </div>

      {open && (
        <div
          data-testid="sidebar-overlay"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
        />
      )}
    </div>
  );
}
